"use client";

/**
 * Affiliate sign-up, step by step like the customer sign-up: 1 Account (Google, or email + password),
 * 2 About you (name), 3 Verify (the emailed code). The left side explains the program.
 */
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Lock, Mail, RotateCw, UserRound, ArrowLeft } from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { affApi, setAffToken } from "@/lib/affiliatePortal";
import {
  Alert,
  AuthHeading,
  AuthShell,
  BackButton,
  Field,
  OrDivider,
  OtpInput,
  PasswordField,
  StepBar,
  SubmitButton,
} from "@/components/auth/AuthUI";

const STEPS = ["Account", "About you", "Verify"];
const RESEND_WAIT = 30;
type Step = "account" | "profile" | "verify";
type Errors = { email?: string; password?: string; name?: string };

export default function AffiliateSignupPage() {
  const [step, setStep] = useState<Step>("account");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [wait, setWait] = useState(0);
  const router = useRouter();

  const toDash = useCallback(() => router.replace("/affiliate/dashboard"), [router]);
  const showError = useCallback((m: string) => setError(m), []);
  const clear = (k: keyof Errors) => setErrors((e) => ({ ...e, [k]: undefined }));

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  /** Step 1 -> 2: check the email and password here. */
  function onAccount(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const next: Errors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "That doesn't look like an email address.";
    if (password.length < 8) next.password = "Use at least 8 characters.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setStep("profile");
  }

  /** Step 2 -> 3: create the account, then ask for the emailed code. */
  async function onProfile(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setErrors({ name: "Tell us your name." });
      return;
    }
    setLoading(true);
    try {
      await affApi.signup(name.trim(), email.trim(), password);
      setStep("verify");
      setCode("");
      setNotice(`We sent a 6-digit code to ${email.trim()}.`);
      setWait(RESEND_WAIT);
    } catch (err) {
      const m = err instanceof Error ? err.message : "Sign-up failed.";
      setError(m);
      if (/email|exists/i.test(m)) setStep("account");
    } finally {
      setLoading(false);
    }
  }

  async function verify(value = code) {
    if (value.length !== 6 || loading) return;
    setError(null);
    setLoading(true);
    try {
      const r = await affApi.verify(email.trim(), value);
      setAffToken(r.token);
      router.replace("/affiliate/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed.");
      setCode("");
      setLoading(false);
    }
  }

  async function onResend() {
    if (wait > 0) return;
    setError(null);
    try {
      await affApi.resend(email.trim());
      setNotice("A new code is on its way.");
      setWait(RESEND_WAIT);
      setCode("");
    } catch {
      setError("Couldn't send a new code.");
    }
  }

  const legal = (
    <>
      By joining you agree to our{" "}
      <Link href="/terms#referrals" className="font-semibold underline underline-offset-2 hover:text-[#4b33e0]">Terms</Link>{" "}and{" "}
      <Link href="/privacy" className="font-semibold underline underline-offset-2 hover:text-[#4b33e0]">Privacy Policy</Link>.
    </>
  );

  if (step === "verify")
    return (
      <AuthShell mode="affiliate" footer={legal}>
        <StepBar steps={STEPS} current={2} />
        <AuthHeading
          title="Check your email"
          sub={<>Enter the 6-digit code we sent to <strong className="font-extrabold text-ink">{email}</strong>.</>}
        />
        <form onSubmit={(e) => { e.preventDefault(); verify(); }} className="rbl-step space-y-3">
          <OtpInput value={code} onChange={setCode} onComplete={verify} invalid={!!error} disabled={loading} />
          {error ? <Alert tone="error" title="That code didn't work">{error}</Alert>
            : notice ? <Alert tone="success">{notice}</Alert> : null}
          <SubmitButton loading={loading} disabled={code.length !== 6} loadingText="Checking code…">Verify &amp; open my portal</SubmitButton>
        </form>
        <div className="mt-4 flex items-center justify-between gap-3 text-[0.85rem]">
          <button type="button" onClick={() => { setStep("account"); setError(null); setNotice(null); setCode(""); }}
            className="inline-flex items-center gap-1.5 font-bold text-ink-soft transition-colors hover:text-[#4b33e0]">
            <ArrowLeft size={15} /> Change email
          </button>
          <button type="button" onClick={onResend} disabled={wait > 0}
            className="inline-flex items-center gap-1.5 font-extrabold text-violet-deep transition-opacity hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-55">
            <RotateCw size={14} />{wait > 0 ? `Send a new code in ${wait}s` : "Send a new code"}
          </button>
        </div>
        <p className="mt-4 rounded-2xl bg-[#f6f4fd] px-3.5 py-2.5 text-[0.78rem] leading-relaxed text-ink-soft">
          Can&apos;t find it? Check your spam or promotions folder, or search your inbox for “Rankbist”.
        </p>
      </AuthShell>
    );

  if (step === "profile")
    return (
      <AuthShell mode="affiliate" footer={legal}>
        <StepBar steps={STEPS} current={1} />
        <AuthHeading title="Tell us about you" sub="Your name, for your portal and your payouts. Then we'll email you a code." />
        <form onSubmit={onProfile} className="rbl-step space-y-4" noValidate>
          <Field id="aff-name" label="Full name" icon={UserRound} autoComplete="name" placeholder="Your full name" autoFocus
            value={name} error={errors.name} onChange={(e) => { setName(e.target.value); clear("name"); }} />
          {error && <Alert tone="error" title="Couldn't create your account">{error}</Alert>}
          <div className="pt-1.5">
            <SubmitButton loading={loading} loadingText="Sending your code…">Create affiliate account</SubmitButton>
          </div>
        </form>
        <BackButton onClick={() => { setStep("account"); setError(null); }}>Back</BackButton>
      </AuthShell>
    );

  return (
    <AuthShell mode="affiliate" footer={legal}>
      <StepBar steps={STEPS} current={0} />
      <AuthHeading title="Join as an affiliate" sub="Free to join. Get your link in under a minute." />
      <GoogleSignInButton
        text="signup_with"
        label="Sign up with Google"
        exchange={async (cred) => { const r = await affApi.google(cred); setAffToken(r.token); }}
        onSuccess={toDash}
        onError={showError}
      />
      <OrDivider label="or use email" />
      <form onSubmit={onAccount} className="rbl-step space-y-4" noValidate>
        <Field id="aff-email" label="Email" icon={Mail} type="email" autoComplete="email" placeholder="you@example.com"
          value={email} error={errors.email} onChange={(e) => { setEmail(e.target.value); clear("email"); }} />
        <PasswordField id="aff-password" icon={Lock} value={password} onChange={(v) => { setPassword(v); clear("password"); }}
          error={errors.password} meter minLength={8} autoComplete="new-password" placeholder="At least 8 characters" />
        {error && <Alert tone="error" title="Something went wrong">{error}</Alert>}
        <div className="pt-1.5">
          <SubmitButton loading={false} loadingText="">Continue</SubmitButton>
        </div>
      </form>
      <p className="mt-4 text-center text-[0.88rem] text-[#5c5878]">
        Already an affiliate?{" "}
        <Link href="/affiliate/login" className="font-extrabold text-[#4b33e0] underline-offset-4 hover:underline">Sign in</Link>
      </p>
    </AuthShell>
  );
}
